/**
 * TeamSystem Impact - App Routes
 * Configurazione routing principale
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { LoginPage } from './modules/auth/LoginPage';
import { AuthCallback } from './modules/auth/AuthCallback';
import { OAuthConsentPage } from './modules/oauth/OAuthConsentPage';
import { DashboardLayout } from './shared/components/Layout/DashboardLayout';
import { Dashboard } from './modules/dashboard/Dashboard';
import { DMAPage } from './modules/dma';
import { SettingsRouter } from './modules/settings/SettingsRouter';
import { PrivateRoute } from './shared/components/PrivateRoute';
import { useAuthStore } from './store/authStore';
import { isReadOnlyRole } from './utils/permissions';

function AdminRoute({ children }: { children: React.ReactNode }) {
  const utente = useAuthStore((s) => s.utente);
  const isAdmin = utente?.ruolo === 'amministratore' || utente?.ruolo === 'responsabile_esg';
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Consente l'accesso agli admin e ai ruoli di sola lettura (visualizzatore,
 * legal_reviewer). Usato per pagine di sola consultazione come il Report DMA:
 * il ruolo decide cosa si vede, la pagina non espone azioni di scrittura.
 */
function AdminOrReadOnlyRoute({ children }: { children: React.ReactNode }) {
  const utente = useAuthStore((s) => s.utente);
  const ruolo = utente?.ruolo;
  const isAdmin = ruolo === 'amministratore' || ruolo === 'responsabile_esg';
  if (!isAdmin && !isReadOnlyRole(ruolo)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
import { DesignSystemShowcase } from './modules/design-system/DesignSystemShowcase';

// Lazy-loaded route modules for bundle optimization (WS1.1)
const InboxPage = lazy(() => import('./modules/inbox/InboxPage').then(m => ({ default: m.InboxPage })));
const DataCapturePage = lazy(() => import('./modules/data-capture/DataCapturePage').then(m => ({ default: m.DataCapturePage })));
const EngagementPage = lazy(() => import('./modules/engagement/EngagementPage').then(m => ({ default: m.EngagementPage })));
const MAPage = lazy(() => import('./modules/ma/MAPage').then(m => ({ default: m.MAPage })));
const MADealDetail = lazy(() => import('./modules/ma/MADealDetail').then(m => ({ default: m.MADealDetail })));
import { ReportingRouter } from './modules/reporting/ReportingRouter';

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
    <CircularProgress />
  </Box>
);

const RootRedirect = () => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
};

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/*
        Consenso per i client AI (connettore MCP). Sta fuori dal layout perché è
        una schermata a sé, e fuori dal prefisso /oauth/ perché nginx inoltra
        tutto quel prefisso al backend: una rotta React lì non verrebbe servita.
        L'autenticazione la gestisce la pagina, che tenta prima l'accesso
        silenzioso e poi manda al login conservando i parametri.
      */}
      <Route path="/connect/consent" element={<OAuthConsentPage />} />

      {/* Protected routes - Layout senza PrivateRoute wrapper */}
      <Route element={<DashboardLayout />}>
        <Route
          path="dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        {/* Inbox - Notifications (lazy-loaded) */}
        <Route
          path="inbox"
          element={
            <PrivateRoute>
              <Suspense fallback={<LazyFallback />}>
                <InboxPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        {/* DMA Module - Double Materiality Assessment */}
        <Route
          path="dma/*"
          element={
            <PrivateRoute>
              <DMAPage />
            </PrivateRoute>
          }
        />
        <Route
          path="data/*"
          element={
            <PrivateRoute>
              <Suspense fallback={<LazyFallback />}>
                <DataCapturePage />
              </Suspense>
            </PrivateRoute>
          }
        />
        {/* Backward compatibility: redirect old data-capture paths to /data */}
        <Route
          path="data-capture/*"
          element={<Navigate to="/data" replace />}
        />
        <Route
          path="reporting/*"
          element={
            <PrivateRoute>
              <AdminOrReadOnlyRoute>
                <ReportingRouter />
              </AdminOrReadOnlyRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="taxonomy/*"
          element={
            <PrivateRoute>
              <AdminRoute>
                <div>Modulo Tassonomia EU (Coming Soon)</div>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="stakeholders"
          element={
            <PrivateRoute>
              <AdminRoute>
                <div>Stakeholder</div>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        {/* Engagement - Stakeholder Engagement (admin only, lazy-loaded) */}
        <Route
          path="engagement"
          element={
            <PrivateRoute>
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}>
                  <EngagementPage />
                </Suspense>
              </AdminRoute>
            </PrivateRoute>
          }
        />
        {/* M&A - fascicoli di due diligence (sola lettura, lazy-loaded).
            Nessun AdminRoute: l'autorizzazione è l'ACL per-deal lato server —
            chi non è autorizzato riceve una lista vuota e un 404 sul dettaglio. */}
        <Route
          path="ma"
          element={
            <PrivateRoute>
              <Suspense fallback={<LazyFallback />}>
                <MAPage />
              </Suspense>
            </PrivateRoute>
          }
        />
        <Route
          path="ma/:dealId"
          element={
            <PrivateRoute>
              <Suspense fallback={<LazyFallback />}>
                <MADealDetail />
              </Suspense>
            </PrivateRoute>
          }
        />
        {/* Backward compat: agent-tokens redirects to settings/connections */}
        <Route
          path="agent-tokens"
          element={<Navigate to="/settings/connections" replace />}
        />
        <Route
          path="settings/*"
          element={
            <PrivateRoute>
              <AdminRoute>
                <SettingsRouter />
              </AdminRoute>
            </PrivateRoute>
          }
        />
      </Route>

      {/* Design System showcase (design branch only) */}
      <Route path="/design-system" element={<DesignSystemShowcase />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
